/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {WorkBook} from 'xlsx/types';
import XLSX from 'xlsx/xlsx';
import {CarbDevices, GenericObject, SentinelDevices} from '../types/entities';
import {DropsheetOptions} from '../types/templates';
import {DateType} from '../utils/DateUtil';
import {isEqual, titleCase, toCamelCase} from '../utils/StringUtil';

export type XLSXSheetToJSONType = XLSX.utils.sheet_to_json;

// defines the type definitions for serialize work book result ...
export type SerializeWorkbookResult = {
  [key: string]: XLSXSheetToJSONType;
};

// defines the type definitions for normalized attributes ...
export type NormalizedAttributeType = {
  [key: string]: string;
};

// determines if the binary FileReader is supported in the given UA ...
const BINARY_READ_AVAILABLE: boolean = Boolean(
  typeof FileReader !== 'undefined' && FileReader.prototype && FileReader.prototype.readAsBinaryString
);

const CSV_FILE_TYPE = 'text/csv';

// defines the default newline token used when fetching data from a csv ...
const LINE_SEPARATOR   = '\r\n';

/**
 * The serializeWorkbook function allows for serializing a given XLSX
 * workbook to either a JSON, CSV, or HTML representation. By default,
 * the serialization format is JSON.
 *
 * ```typescript
 *
 * import {serializeWorkbook} from '@ion/core/src/xlsx';
 *
 * const serializedWorkbook = serializeWorkbook(wb, format);
 * ```
 *
 * @param  workbook The XSLX workbook which is to be serialized.
 * @param  format The format by which the workbook is to be
 *         serialized, valid values are: 'json', 'csv', 'html'.
 * @return The serialized workbook result.
 */
export const serializeWorkbook = (
  workbook: WorkBook,
  format: string = 'json',
  isDevice: boolean
): SerializeWorkbookResult => {
  const result = {};
  workbook.SheetNames.forEach(sheetName => {
    const options = {
      header    : 1,
      blankrows : false
    };
    if (isDevice) {
      options.range = 9;
    }
    const roa = XLSX.utils[`sheet_to_${format}`](workbook.Sheets[sheetName], options);
    if (roa.length > 0) {
      result[sheetName] = roa;
    }
  });
  return result;
};

/**
 * The parseRAWData function allows for parsing a raw data stream to a
 * recognizable format which can then be serialized to another format.
 *
 * ```typescript
 *
 * import {parseRAWData} from '@ion/core/src/xlsx';
 *
 * const parsedData = parseRAWData(data);
 * ```
 *
 * @param   data The raw data stream which is to be processed.
 * @param   delimiter The delimiter value.
 * @returns The parsed string processed from the provided data.
 */
export const parseRAWData = (data: any, delimiter: number = 10240): string => {
  let val = '';
  for (let i = 0; i < data.byteLength / delimiter; ++i) {
    val += String.fromCharCode.apply(null,
      new Uint8Array(data.slice(i * delimiter, i * delimiter + delimiter)));
  }
  val += String.fromCharCode.apply(null, new Uint8Array(data.slice(val.length)));
  return val;
};

/**
 * The map function provides a utility which maps arrays of entities
 * from an xlsx spreadsheet import to an array of objects based on
 * the provided headers which represent the entity properties.
 *
 * ```typescript
 *
 * import {map} from '@ion/core/src/xlsx';
 *
 * const mapped = map(data);
 * ```
 *
 * @param   props An array of entity properties which were used
 *          as the spreadsheet headers from the xlsx import.
 * @param   items An array of arrays which represent each entity
 *          row from the spreadsheet import. Each array will be converted
 *          to an object with properties mapped to each field within the
 *          props array.
 * @param   converter A converter function which is to be used
 *          for converting column header names to entity attribute names.
 * @returns An array of objects based on the header properties.
 */
export const map = (
  props: Array<any>,
  items: Array<any>,
  converter: Function
): Array<any> => {
  const results   = [];
  const translate = converter || toCamelCase;
  items.forEach((val) => {
    const item = {};
    props.forEach((prop, i) => {
      item[translate(prop)] = val[i];
    });
    results.push(item);
  });
  return results;
};

/**
 * The process function handles processing an XLSX document to an Array of
 * objects, whereby the first object in the array contains the headers of
 * the spreadsheet, and each additional object represents a row within the
 * spreadsheet.
 *
 * ```typescript
 *
 * import {process} from '@ion/core/src/xlsx';
 *
 * process(files, importerOptions);
 * ```
 *
 * @param   files The raw data stream of files to be processed.
 *          For additional information, see:
 *          https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer/files
 * @param   options.onComplete A handler to be invoked when the
 *          operation has been completed. The function will be invoked within
 *          the following argument:
 *           items: The parsed spreadsheet which contains the spreadsheet
 *           headers as the first element, and each spreadsheet row as the
 *           additional elements.
 *           workbook: The xlsx workbook which contains all information
 *           related to the uploaded spreadsheet.
 *           workbook: The xlsx workbook which contains all information
 *           related to the uploaded spreadsheet.
 * @param   options.onFailed A handler which will be invoked if
 *          the operation fails.
 * @param   options.parse By default, the imported spread sheet will
 *          be converted to an array of arrays, whereby the first array contains
 *          the spreadsheet column headers, and the subsequent arrays represent
 *          each row within the spreadsheet. This can then be parsed freely for
 *          further processing (such as sending the results to a Service, etc.)
 *          However, the processing of the results can be handled automatically
 *          when this option is set to true, in which case and array of objects
 *          will be returned with each items values mapped to each header
 *          property.
 * @param   options.converter A converter function which is to be
 *          used for converting column header names to entity attribute names.
 *          Note, this option is ignored if options.parse is false, otherwise,
 *          if options.parse is true and a converter function is provided, the
 *          function will be invoked with each column name for processing column
 *          header names to entity attribute names. By default, if a converter
 *          function is not provided, the header names are converted to camelCase
 *          when parsing the results. Use this option for providing more robust
 *          conversions.
 */
export const process = (files: FileList, options: DropsheetOptions, isDevice: boolean = false) => {
  const {onComplete, onFailed, onSizeError, parse, format, maxSize, convertCSVToJSON} = options;
  let i, file;
  for (i = 0, file = files[i]; i !== files.length; ++i) {
    const reader = new FileReader();
    reader.onload = (evt) => {
      let data = evt.target.result;
      let wb: XLSX.read, arr: string;
      const identifier = options?.isAssetBulk ? 'assetIdentifier' : 'deviceIdentifier';
      const readtype = {
        type : BINARY_READ_AVAILABLE ? 'binary' : 'base64'
      };
      if (!BINARY_READ_AVAILABLE) {
        arr  = parseRAWData(data);
        data = btoa(arr);
      }
      const processCsvUpload = () => {
        try {
          const dataString = data.toString().split(LINE_SEPARATOR);
          const csvData    = dataString.filter(item => !isEqual(item.trim(), ''));
          if (isDevice && csvData?.length > 6) {
            csvData.splice(0, 5);
          }

          if (convertCSVToJSON) {
            wb = XLSX.read(data, readtype);
            const sheet = wb.SheetNames[0];
            const json  = serializeWorkbook(wb, format, isDevice)[sheet];
            if (parse) {
              const props = json.shift();
              onComplete(map(props, json, options.converter), wb, files);
            } else {
              onComplete(json, wb, files);
            }
          } else {
            const heading = csvData[0];
            const json    = [];
            csvData?.forEach(item => {
              if (!isEqual(item, heading)) {
                json.push({
                  [identifier] : item
                });
              }
            });
            onComplete(json, {}, files);
          }
        } catch (err) {
          onFailed(err);
        }
      };

      const processFileUpload = () => {
        try {
          wb = XLSX.read(data, readtype);
          const sheet = wb.SheetNames[0];
          const json  = serializeWorkbook(wb, format, isDevice)[sheet];
          if (parse) {
            const props = json.shift();
            onComplete(map(props, json, options.converter), wb, files);
          } else {
            onComplete(json, wb, files);
          }
        } catch (err) {
          onFailed(err);
        }
      };
      const size = (evt.target?.result as string)?.length;
      if (size > maxSize) {
        onSizeError(size);
      } else {
        if (isEqual(CSV_FILE_TYPE, files[0]?.type)) {
          processCsvUpload();
        } else {
          processFileUpload();
        }
      }
    };
    if (BINARY_READ_AVAILABLE) {
      reader.readAsBinaryString(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  }
};

/**
 * The formatHeaders method accepts an object, and returns all of it's
 * properties as titles which are suitable for use as Spreadsheet column
 * headers; for example, 'assetName' becomes 'Asset Name', etc.
 *
 * ```typescript
 *
 * import {formatHeaders} from '@ion/core/src/xlsx';
 *
 * const fomrattedHeaders = formatHeaders(headers);
 * ```
 *
 * @param  headers The object which is to have properties returned
 *         as column header titles.
 * @param  formatter A formatter function which processes each
 *         header name to a specific string to be displayed. If this argument
 *         is omitted, the titleCase function will be used, which will simply
 *         convert camelCase names to title formatted strings; e.g. "propA",
 *         will be converted to "Prop A".
 * @return An array of all properties as formatted column titles.
 */
export const formatHeaders = (headers: Array<string>,
  formatter?: (val: string) => string): string[] => {
  const converter = formatter || titleCase;
  return headers.map(converter);
};

/**
 * Provides a generalized utility for converting imported column headers to
 * their respective keys (CTC attributes). This utility can be used by an
 * Import Container's converter implementation.
 *
 * ```typescript
 *
 * import {normalizeHeaders} from '@ion/core/src/xlsx';
 *
 * const normalizeHeaders = normalizeHeaders(column, attributes);
 * ```
 *
 * @param column the column whose header is to be normalized.
 * @param attributes map of keys (CTC attributes) that has to replaced
 * with the normalized headers.
 * @return the normalized header.
 */
export const normalizeHeaders = (
  column: string,
  attributes: GenericObject,
  extractSubstring: boolean = true
): string => {
  const pattern = column.match(/[(]/);
  if (pattern && extractSubstring) {
    column = column.substr(0, pattern.index-1);
  }
  if (column.indexOf('__group__') > -1) {
    column = column.replace('__group__', '').trim();
  }
  return attributes[column];
};

/**
 * Provides a generalized utility for normalizing column headers and mapping
 * them to their respective keys (CTC attributes). This utility can be used
 * to remove localization tokens from column headers; for example, normalize
 * "Category __group__" to "Category", etc.
 *
 * ```typescript
 *
 * import {normalizeAttributes} from '@ion/core/src/xlsx';
 *
 * const normalizeHeaders = normalizeAttributes(attributes);
 * ```
 *
 * @param attributes object which has to be normalized for generating
 * the mapping of keys(CTC attributes) against column headers.
 * @return the normalized attribute map that can be used to
 * normalize headers.
 */
export const normalizeAttributes = (attributes: GenericObject): NormalizedAttributeType => {
  const normalized = {};
  Object.keys(attributes).forEach(column => {
    const attribute = attributes[column];
    if (column.indexOf('__group__') > -1) {
      column = column.replace('__group__', '').trim();
    }
    if (column.indexOf('Optional') > -1) {
      const term = column.includes('s (Optional)') ? 's (Optional)' : ' (Optional)';
      column = column.replace(term, '').trim();
    }
    normalized[column] = attribute;
  });
  return normalized;
};

/**
 * Provides a generalized utility for parsing date values.
 *
 * ```typescript
 *
 * import {parseDate} from '@ion/core/src/xlsx';
 *
 * const parsedData = parseDate(val, date);
 * ```
 *
 * @param date value that has to be parsed against a date type.
 * @param date1904 the date type using which the value is parsed.
 * @return the parsed date value.
 */
export const parseDate = (date: any, date1904?: DateType): number => {
  const utcDate: any = new Date(Date.UTC(1899, 11, 30));
  return (Date.parse(date1904 ? date : (date += 1462)) - utcDate) / (24 * 60 * 60 * 1000);
};

/**
 * Provides a generalized utility for generating excel sheets.
 *
 * ```typescript
 *
 * import {toSheets} from '@ion/core/src/xlsx';
 *
 * const sheets = toSheets(entries);
 * ```
 *
 * @param data for generating excel sheets.
 * @return the sheets generated with the data.
 */
export const toSheets = (data: any[]): XLSX.utils.encode_range => {
  const ws     = {};
  const range  = {s: {c: 10000000, r: 10000000}, e: {c: 0, r: 0}};
  const length = data.length;
  for (let R = 0; R !== length; ++R) {
    const n = data[R].length;
    for (let C = 0; C !== n; ++C) {
      const {s, e} = range;
      if (s.r > R) s.r = R;
      if (s.c > C) s.c = C;
      if (e.r < R) e.r = R;
      if (e.c < C) e.c = C;
      const cell: GenericObject = {v: data[R][C]};
      if (cell.v !== null) {
        const cell_ref = XLSX.utils.encode_cell({c: C, r: R});
        if (typeof cell.v === 'number') {
          cell.t = 'n';
        } else if (typeof cell.v === 'boolean') {
          cell.t = 'b';
        } else if (cell.v instanceof Date) {
          cell.t = 'n';
          cell.z = XLSX.SSF._table[14];
          cell.v = parseDate(cell.v);
        } else {
          cell.t = 's';
        }
        ws[cell_ref] = cell;
      }
    }
  }
  if (range.e.c < 10000000) {
    ws['!ref'] = XLSX.utils.encode_range(range);
  }
  return ws;
};

/**
 * Provides a generalized utility for generating buffers within workbook.
 *
 * ```typescript
 *
 * import {s2ab} from '@ion/core/src/xlsx';
 *
 * const buffer = s2ab(wbout);
 * ```
 *
 * @param  sheet workbook sheet within which buffer is to be generated.
 * @return generated buffer.
 */
export const s2ab = (sheet: XLSX.write): ArrayBuffer => {
  const buf  = new ArrayBuffer(sheet.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i !== sheet.length; ++i) {
    view[i] = sheet.charCodeAt(i) & 0xFF;
  }
  return buf;
};

/**
 * Provides a utility which, given a CSV string, returns an array containing
 * each item.
 *
 * ```typescript
 *
 * import {fromCSV} from '@ion/core/src/xlsx';
 *
 * fromCSV('US');
 * ```
 */
export const fromCSV = (csv: string): string[] => {
  return csv.split(',').map(entry => entry.trim());
};

/**
 * Provides a utility that, given a pasted Ids array and entity string,
 * returns a Blob object.
 *
 * ```typescript
 *
 * import {getCustomWorkBook} from '@ion/core/src/xlsx/ExcelHelpers';
 *
 * getCustomWorkBook(['98', '980'], Entity.device);
 * ```
 */
export const getCustomWorkBook = (
  pastedIds: string[] | SentinelDevices | CarbDevices,
  entity: string,
  isMultiple: boolean = false,
  actionType: string = 'sentinel',
  columns: string[] = ['identifiersList', 'Secondary identifiers(optional)']
// eslint-disable-next-line max-params
): Blob => {
  let entries = !isMultiple ? [['identifiersList']] : [columns];
  if (isMultiple) {
    entries = getNewEntries(entries, pastedIds, actionType);
  } else {
    pastedIds.forEach((id) => entries.push([id]));
  }
  const wb1 = {
    SheetNames : [entity],
    Sheets     : {
      [entity] : toSheets(entries)
    }
  };
  const wbout = XLSX.write(wb1, {
    bookType : 'xlsx',
    bookSST  : true,
    type     : 'binary'
  });
  const buffer = s2ab(wbout);
  const customWorkBook = new Blob(
    [buffer],
    {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
  );

  return customWorkBook;
};

const getNewEntries = (entries, pastedIds, actionType) => {
  const newRows = actionType === 'sentinel'
    ? (pastedIds as SentinelDevices).map(({primaryDevice, secondaryDevice}) =>
      [primaryDevice, secondaryDevice]
    )
    : (pastedIds as CarbDevices).map(({identifier, vin, licensePlate, licenseState, registrationRenewalDate}) =>
      [identifier, vin, licensePlate, licenseState, registrationRenewalDate]
    );

  return [...entries, ...newRows];
};

/**
 * Provides a utility that, given a filedata and sheet name string,
 * returns a CSV Blob object.
 *
 * ```typescript
 *
 * import {getParametersWorkBook} from '@ion/core/src/xlsx/ExcelHelpers';
 *
 * getParametersWorkBook([id:'98', index:'980']);
 * ```
 */
export const getParametersWorkBook = (
  fileData
): SerializeWorkbookResult => {
  const csvStr = getCsvString(fileData) + '\r\n';
  const csvBlob = new Blob([csvStr], {type: 'text/csv'});
  return csvBlob;
};

const getCsvString = (fileData): string => {
  const csvRows = [];
  const headers = ['Parameter Id', 'Parameter Index', 'Parameter Value'];
  csvRows.push(headers.join(','));

  fileData.forEach(row => {
    const values = headers.map(header => {
      const value = row[header];
      return Array.isArray(value) ? value.join(';') : value;
    });
    csvRows.push(values.join(','));
  });
  return csvRows.join('\r\n');
};
